import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { haptics } from '../utils/haptics';

/**
 * App-level toasts:
 *  - "New version available" snackbar when the service worker has an
 *    update waiting (dispatched from index.js as 'sw-update-available')
 *  - offline banner driven by online/offline events
 */
const AppToasts = () => {
  const { language } = useLanguage();
  const [waitingRegistration, setWaitingRegistration] = useState(null);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' && !navigator.onLine
  );

  useEffect(() => {
    const onUpdate = (e) => setWaitingRegistration(e.detail);
    window.addEventListener('sw-update-available', onUpdate);
    return () => window.removeEventListener('sw-update-available', onUpdate);
  }, []);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  const handleRefresh = () => {
    haptics.tap();
    const waiting = waitingRegistration?.waiting;
    if (waiting) {
      // reload once the new SW takes control
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      }, { once: true });
      waiting.postMessage({ type: 'SKIP_WAITING' });
      setWaitingRegistration(null);
    } else {
      window.location.reload();
    }
  };

  return (
    <>
      {isOffline && (
        <div className="offline-banner" role="status">
          <i className="bi bi-wifi-off" />
          {language === 'en'
            ? 'Offline — showing saved data'
            : 'Εκτός σύνδεσης — εμφάνιση αποθηκευμένων δεδομένων'}
        </div>
      )}

      {waitingRegistration && (
        <div className="update-toast" role="alert">
          <div className="update-toast-text">
            <i className="bi bi-arrow-repeat" />
            {language === 'en' ? 'A new version is available' : 'Διαθέσιμη νέα έκδοση'}
          </div>
          <button className="update-toast-btn" onClick={handleRefresh}>
            {language === 'en' ? 'Refresh' : 'Ανανέωση'}
          </button>
          <button
            className="update-toast-dismiss"
            onClick={() => setWaitingRegistration(null)}
            aria-label="Dismiss"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      )}
    </>
  );
};

export default AppToasts;
